/**
 * Sistema de verificación de versión para forzar la recarga cuando hay una nueva versión
 */

interface VersionInfo {
  version: string;
  buildTime: string;
}

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos
const VERSION_KEY = 'app_version';
const BUILD_TIME_KEY = 'app_build_time';

/**
 * Obtiene la versión actual desde el servidor
 */
async function fetchCurrentVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });
    
    if (!response.ok) {
      return null;
    }
    
    const versionInfo: VersionInfo = await response.json();
    return versionInfo;
  } catch (error) {
    console.error('Error fetching version:', error);
    return null;
  }
}

/**
 * Obtiene la versión almacenada en localStorage
 */
function getStoredVersion(): VersionInfo | null {
  try {
    const version = localStorage.getItem(VERSION_KEY);
    const buildTime = localStorage.getItem(BUILD_TIME_KEY);
    
    if (!version || !buildTime) {
      return null;
    }
    
    return { version, buildTime };
  } catch (error) {
    return null;
  }
}

/**
 * Guarda la versión en localStorage
 */
function storeVersion(versionInfo: VersionInfo): void {
  try {
    localStorage.setItem(VERSION_KEY, versionInfo.version);
    localStorage.setItem(BUILD_TIME_KEY, versionInfo.buildTime);
  } catch (error) {
    console.error('Error storing version:', error);
  }
}

/**
 * Verifica si hay una nueva versión disponible
 */
async function checkForNewVersion(): Promise<boolean> {
  const currentVersion = await fetchCurrentVersion();
  
  if (!currentVersion) {
    return false;
  }
  
  const storedVersion = getStoredVersion();
  
  // Si no hay versión almacenada, guardar la actual
  if (!storedVersion) {
    storeVersion(currentVersion);
    return false;
  }
  
  // Comparar versiones: si la versión o el buildTime son diferentes, hay una nueva versión
  const hasNewVersion = 
    currentVersion.version !== storedVersion.version ||
    currentVersion.buildTime !== storedVersion.buildTime;
  
  if (hasNewVersion) {
    storeVersion(currentVersion);
    return true;
  }
  
  return false;
}

/**
 * Fuerza la recarga de la página
 */
function forceReload(): void {
  // Limpiar cache del navegador
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
  
  // Recargar la página
  window.location.reload();
}

/**
 * Inicia el sistema de verificación de versión
 */
export function startVersionChecker(): void {
  // Verificar inmediatamente al cargar
  checkForNewVersion().then((hasNewVersion) => {
    if (hasNewVersion) {
      console.log('Nueva versión detectada. Recargando...');
      forceReload();
    }
  });
  
  // Verificar periódicamente
  setInterval(() => {
    checkForNewVersion().then((hasNewVersion) => {
      if (hasNewVersion) {
        console.log('Nueva versión detectada. Recargando...');
        forceReload();
      }
    });
  }, VERSION_CHECK_INTERVAL);
}

/**
 * Obtiene la versión actual (útil para debugging)
 */
export function getCurrentVersion(): VersionInfo | null {
  return getStoredVersion();
}


